import {useCallback, useEffect, useRef} from "react";
import {useLocation, useNavigate, useNavigationType} from "react-router-dom";

// marks the sentinel history entry this guard keeps on top of the stack.
const GUARD_STATE_KEY = "elevBackGuard";

// what the guard needs to know about a history entry: the router's position in the stack
// (react-router stores it as state.idx) and whether the entry is one of our sentinels. The
// sentinel copies its twin's idx, so a pop between the two reads as the same position.
type Entry = {idx: number | null; guard: boolean};

function entryOf(state: any): Entry {
    return {idx: state?.idx ?? null, guard: !!state?.[GUARD_STATE_KEY]};
}

function pushGuardEntry() {
    window.history.pushState({...window.history.state, [GUARD_STATE_KEY]: true}, "", window.location.href);
}

// the anchor a click would follow, when following it leaves the tool's page inside the app.
// Clicks that open a new tab, download, or go to another origin (the browser's own
// beforeunload prompt covers those) are not the guard's business, nor is a link that stays on
// the tool's path (a hash or query change).
function leavingAnchor(e: MouseEvent): HTMLAnchorElement | null {
    if(e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return null;

    const target = e.target as Element | null;
    const a = target?.closest?.("a[href]") as HTMLAnchorElement | null;
    if(!a) return null;
    if(a.target && a.target !== "_self") return null;
    if(a.hasAttribute("download")) return null;

    const url = new URL(a.href, window.location.href);
    if(url.origin !== window.location.origin) return null;
    if(url.pathname === window.location.pathname) return null;

    return a;
}

// ask before the tool is left with unsaved changes. The app runs on a plain BrowserRouter
// (no useBlocker), so the guard covers the two in-app ways out itself:
//
// Links: a click on an anchor that leaves the tool's path is stopped in the capture phase,
// before react-router's Link sees it, and onLeave gets an exit that re-sends the click.
//
// Back: the guard keeps a sentinel entry on top of the history stack that duplicates the
// tool's own url. Back then pops onto the tool's url, not off the page:
//  - dirty: the sentinel is pushed again and onLeave runs with no exit (the caller's default)
//  - clean: one more back() carries the user to where Back would have gone
// The sentinel is pushed on mount and after every in-tool push/replace navigation (e.g. a
// workspace switch) so it stays on top. A pop never pushes: the popstate handler owns that
// case, and a push there would race its back().
//
// Forward is never blocked. Entries can only sit ahead of the tool after a pop the guard let
// stand (a clean Back onto an earlier workspace's sentinel, or a Forward from there); when the
// tool then becomes dirty the sentinel is pushed again, which drops them, so there is nothing
// to go forward to while edits are unsaved. A Forward from a clean tool is let through (a pop
// to a higher idx).
//
// The caller opens its pending-changes prompt on onLeave and runs the exit once the user has
// saved or discarded. A null exit means the caller's own way out (its close button's target).
export function useLeaveGuard(props: {dirty: boolean; onLeave: (exit: (() => void) | null) => void}) {
    const loc = useLocation();
    const navType = useNavigationType();
    const nav = useNavigate();

    const latest = useRef(props);
    latest.current = props;

    // the entry the tool is on, as of the last push or pop this guard saw.
    const cur = useRef<Entry>(entryOf(window.history.state));

    // true after a pop the guard let stand, so entries may sit ahead of the current one.
    // A push drops them and clears this.
    const forwardPossible = useRef(false);

    const push = useCallback(() => {
        pushGuardEntry();
        cur.current = entryOf(window.history.state);
        forwardPossible.current = false;
    }, []);

    const mounted = useRef(false);
    useEffect(() => {
        const first = !mounted.current;
        mounted.current = true;
        if(first || navType !== "POP") push();
    }, [loc.key, navType, push]);

    useEffect(() => {
        if(props.dirty && forwardPossible.current) push();
    }, [props.dirty, push]);

    useEffect(() => {
        const onPop = (e: PopStateEvent) => {
            const from = cur.current;
            const next = entryOf(e.state);
            cur.current = next;
            forwardPossible.current = true;

            // a different position: Forward, or Back onto an earlier workspace's sentinel
            // or off the tool. Nothing to guard; the twin pop (same idx) is the one we own.
            if(next.idx === null || next.idx !== from.idx) return;

            // Forward from the twin onto its own sentinel
            if(next.guard && !from.guard) return;

            if(latest.current.dirty) {
                push();
                latest.current.onLeave(null);
                return;
            }

            window.history.back();
        };

        window.addEventListener("popstate", onPop);
        return () => window.removeEventListener("popstate", onPop);
    }, [push]);

    // set while the exit re-sends the intercepted click, so it passes this time.
    const passing = useRef(false);

    useEffect(() => {
        const onClick = (e: MouseEvent) => {
            if(passing.current || !latest.current.dirty) return;

            const a = leavingAnchor(e);
            if(!a) return;

            e.preventDefault();
            e.stopPropagation();

            // re-send the click so the anchor's own handler (react-router's Link, or the
            // browser) follows it. The anchor may be gone by then (a closed menu), so fall
            // back to the router with its path.
            const href = new URL(a.href, window.location.href);
            latest.current.onLeave(() => {
                if(a.isConnected) {
                    passing.current = true;
                    try {
                        a.click();
                    } finally {
                        passing.current = false;
                    }
                    return;
                }

                nav(href.pathname + href.search + href.hash);
            });
        };

        document.addEventListener("click", onClick, true);
        return () => document.removeEventListener("click", onClick, true);
    }, [nav]);
}
